import { LoaderIcon, PlusIcon } from "lucide-react";

import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { ProviderAccountManageRow, type ProviderAccountListItem } from "./ProviderAccountManageRow";

export type { ProviderAccountListItem } from "./ProviderAccountManageRow";

/**
 * Shared connected-account manager for provider subscriptions.
 *
 * It keeps the modal focused on account identity and supported actions so
 * provider sections can reuse one layout instead of each owning a bespoke
 * connected-state card.
 */
interface ProviderAccountManagePanelProps {
  readonly accountName?: string;
  readonly accounts: ReadonlyArray<ProviderAccountListItem>;
  readonly accountEmail: string | null;
  readonly planLabel: string | null;
  readonly scopeLabel: string;
  readonly isDefaultForNewChats: boolean;
  readonly defaultScopeLabel: string;
  readonly needsReconnect: boolean;
  readonly isSwitching: boolean;
  readonly isDisconnecting: boolean;
  readonly onAddAccount: () => void;
  readonly onReconnectAccount: (account: ProviderAccountListItem) => void;
  /** See ProviderAccountManageRow: linked reconnect needs server support. */
  readonly canReconnectLinkedAccounts?: boolean;
  readonly onDisconnect: () => void;
  /** Removes a specific linked (non-primary) account row from the additive store. */
  readonly onRemoveAccount?: ((accountId: string) => void) | undefined;
  readonly removingAccountId?: string | null;
}

interface ProviderAccountSelectorProps {
  readonly accounts: ReadonlyArray<ProviderAccountListItem>;
  readonly defaultScopeLabel: string;
  readonly disabled?: boolean;
  readonly onSelectDefault: (account: ProviderAccountListItem) => void;
  readonly role?: "radiogroup" | "presentation";
}

export function ProviderAccountManagePanel(props: ProviderAccountManagePanelProps) {
  const accounts =
    props.accounts.length > 0
      ? props.accounts
      : [
          {
            id: props.accountEmail ?? "connected-codex-account",
            credentialTarget: "primary" as const,
            accountDisplayName: null,
            accountEmail: props.accountEmail,
            planLabel: props.planLabel,
            isDefaultForNewChats: props.isDefaultForNewChats,
            needsReconnect: props.needsReconnect,
            canDisconnect: true,
          },
        ];

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs font-medium text-foreground">Accounts</div>
          <Button
            size="sm"
            variant="outline"
            disabled={props.isSwitching || props.isDisconnecting}
            onClick={props.onAddAccount}
          >
            {props.isSwitching ? <LoaderIcon className="animate-spin" /> : <PlusIcon />}
            {accountActionLabel()}
          </Button>
        </div>
        <div
          data-slot="provider-account-list"
          className="overflow-hidden rounded-md border border-border/70 bg-background/30 divide-y divide-border/70"
        >
          {accounts.map((account) => (
            <ProviderAccountManageRow
              key={account.id}
              account={account}
              defaultScopeLabel={props.defaultScopeLabel}
              isBusy={props.isSwitching || props.isDisconnecting}
              canReconnectLinkedAccount={props.canReconnectLinkedAccounts === true}
              onReconnect={props.onReconnectAccount}
              onDisconnect={props.onDisconnect}
              onRemoveAccount={props.onRemoveAccount}
              isRemoving={props.removingAccountId === account.id}
            />
          ))}
        </div>
        <p className="px-1 text-[11px] leading-5 text-muted-foreground">{props.scopeLabel}</p>
      </div>
    </div>
  );
}

export function ProviderAccountSelector({
  accounts,
  defaultScopeLabel,
  disabled = false,
  onSelectDefault,
  role = "radiogroup",
}: ProviderAccountSelectorProps) {
  if (accounts.length === 0) return null;
  return (
    <div
      role={role}
      aria-label={role === "radiogroup" ? `${defaultScopeLabel} account` : undefined}
      className="divide-y divide-border/60"
    >
      {accounts.map((account) => (
        <ProviderAccountSelectorRow
          key={account.id}
          account={account}
          defaultScopeLabel={defaultScopeLabel}
          disabled={disabled}
          onSelectDefault={onSelectDefault}
        />
      ))}
    </div>
  );
}

function ProviderAccountSelectorRow({
  account,
  defaultScopeLabel,
  disabled,
  onSelectDefault,
}: {
  readonly account: ProviderAccountListItem;
  readonly defaultScopeLabel: string;
  readonly disabled: boolean;
  readonly onSelectDefault: (account: ProviderAccountListItem) => void;
}) {
  const accountDisplayName = account.accountDisplayName ?? null;
  const primaryLabel = accountDisplayName ?? account.accountEmail;
  const rowDisabled = disabled || account.needsReconnect;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={account.isDefaultForNewChats}
      disabled={rowDisabled}
      onClick={() => onSelectDefault(account)}
      className={cn(
        "flex min-h-18 w-full items-center gap-3 px-3 py-3 text-left transition-colors sm:px-4",
        rowDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-muted/30",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "flex size-4 shrink-0 items-center justify-center rounded-full border",
          account.isDefaultForNewChats ? "border-success" : "border-border",
        )}
      >
        {account.isDefaultForNewChats ? (
          <span className="size-1.5 rounded-full bg-success" />
        ) : null}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-medium leading-5 text-foreground">
          {primaryLabel ?? "Identity unavailable"}
        </div>
        <div className="truncate text-xs leading-5 text-muted-foreground">
          {accountSelectorSecondaryLabel({ account, accountDisplayName })}
        </div>
        <ProviderAccountSelectorStatus account={account} defaultScopeLabel={defaultScopeLabel} />
      </div>
    </button>
  );
}

function ProviderAccountSelectorStatus({
  account,
  defaultScopeLabel,
}: {
  readonly account: ProviderAccountListItem;
  readonly defaultScopeLabel: string;
}) {
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-[11px] leading-5 text-muted-foreground">
      {account.planLabel === null ? null : <span>{account.planLabel}</span>}
      {account.planLabel === null ? null : <span aria-hidden="true">·</span>}
      <span className={account.isDefaultForNewChats ? "text-success-foreground" : undefined}>
        {account.needsReconnect
          ? "Reconnect required"
          : account.isDefaultForNewChats
            ? defaultScopeLabel
            : "Connected"}
      </span>
    </span>
  );
}

function accountSelectorSecondaryLabel({
  account,
  accountDisplayName,
}: {
  readonly account: ProviderAccountListItem;
  readonly accountDisplayName: string | null;
}): string {
  if (accountDisplayName !== null && account.accountEmail !== null) return account.accountEmail;
  if (account.needsReconnect) return "Reconnect before using this account.";
  return "Available to your cloud runtimes.";
}

function accountActionLabel(): string {
  return "Add account";
}
